<template>
  <div class="hierarchical-ratings">
    <h4 class="text-lg font-semibold mb-3" v-if="selectedLevel=='module'">Question Table</h4>
    <table class="min-w-full divide-y divide-gray-200 border mb-2" v-if="selectedLevel=='module'">
      <tr v-for="(question, idx) in getQuestions()" :key="question" class="px-4 py-2 text-left">
        <td class="px-4 py-2 text-left bg-gray-800 text-white" style="font-weight: bold;">Q{{ idx + 1 }}</td>
        <td class="px-4 py-2 text-left">{{ question }}</td>
      </tr>
    </table>

    <div class="mb-4">
      <input v-if="selectedLevel == 'module' && !fullPage" v-model="searchQuery" type="text" placeholder="Search..."
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" />
    </div>
    <h4 class="text-lg font-semibold mb-3">Result</h4>
    <!-- Module Level Table -->
    <div v-if="selectedLevel === 'module'" class="mb-8 overflow-x-auto">

      <table class="min-w-full divide-y divide-gray-200 border">
        <thead class="bg-gray-800 text-white">
          <tr>
            <th class="px-4 py-2 text-left" style="width: 380px;">Module (Responded/Invited)</th>
            <th class="px-4 py-2 text-left" style="width: 220px;">Teacher (Responded)</th>
            <th v-for="(question, idx) in questions" :key="question" class="px-4 py-2 text-left" style="width: 150px;">Q{{ idx + 1 }}</th>
            <th class="px-4 py-2 text-left" style="width: 150px;">Average</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <template v-for="module in paginatedModuleResults">
            <tr class="hover:bg-gray-50">
              <td :rowspan="!fullPage ? module.teachers.length + 1 : 1" class="px-4 py-2 border-r">
                <span style="text-decoration: underline;cursor: pointer;" @click="showComments(module.moduleCode)">{{
                  module.moduleCode }} ({{ module.count }}/{{ module.invited }}) </span>
              </td>
              <td class="px-4 py-2 font-bold">Overall</td>
              <td v-for="question in questions" :key="question" class="px-4 py-2">
                {{ formatRating(module.ratings[question]) }}
              </td>
              <td class="px-4 py-2 font-bold">{{ formatRating(averageRating(module.ratings)) }}</td>
            </tr>
            <tr v-for="teacher in module.teachers" :key="teacher.staffId" class="hover:bg-gray-50">
              <td v-if="fullPage" class="px-4 py-2 border-r">
              </td>
              <td class="px-4 py-2">{{ teacher.name }} ({{ teacher.count }})</td>
              <td v-for="question in questions" :key="question" class="px-4 py-2">
                {{ formatRating(teacher.ratings[question]) }}
              </td>
              <td class="px-4 py-2">{{ formatRating(averageRating(teacher.ratings)) }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Department Level Table -->
    <div v-if="selectedLevel === 'department'" class="mb-8 overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 border">
        <thead class="bg-gray-800 text-white">
          <tr>
        <th class="px-4 py-2 text-left" style="width: 250px;">Department</th>
        <th class="px-4 py-2 text-left" style="width: 150px;">Responded/Invited</th>
        <th v-for="(question, idx) in getQuestions()" :key="question" class="px-4 py-2 text-left" style="width: 100px;">Q{{ idx + 1 }}</th>
        <th class="px-4 py-2 text-left" style="width: 100px;">Average</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="dept in paginatedDepartmentResults" :key="dept.name" class="hover:bg-gray-50">
        <td class="px-4 py-2" style="width: 250px;">{{ dept.name }}</td>
        <td class="px-4 py-2" style="width: 150px;">{{ dept.responded }}/{{ dept.invited }}</td>
        <td v-for="question in getQuestions()" :key="question" class="px-4 py-2" style="width: 100px;">
          {{ formatRating(dept.ratings[question]) }}
        </td>
        <td class="px-4 py-2" style="width: 100px;">{{ formatRating(averageRating(dept.ratings)) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- School Level Table -->
    <div v-if="selectedLevel === 'school'" class="mb-8 overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 border">
        <thead class="bg-gray-800 text-white">
          <tr>
            <th class="px-4 py-2 text-left" style="width: 380px;">School</th>
            <th class="px-4 py-2 text-left" style="width: 250px;">Responded/Invited</th>
            <th v-for="(question, idx) in getQuestions()" :key="question" class="px-4 py-2 text-left" style="width: 150px;">Q{{ idx + 1 }}
            </th>
            <th class="px-4 py-2 text-left" style="width: 150px;">Average</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="(ratings, school) in data.schoolResults?.schoolRatings || {}" :key="school"
            class="hover:bg-gray-50">
            <td class="px-4 py-2" >{{ getSchoolName(school) }}</td>
            <td class="px-4 py-2">{{ data.schoolResults.stats[school].responded }}/{{
              data.schoolResults.stats[school].invited }}</td>
            <td v-for="(question, index) in getQuestions()" :key="question" class="px-4 py-2">
              {{ formatRating(getRating(ratings, index)) }}
            </td>
            <td class="px-4 py-2">{{ formatRating(averageRating(ratings)) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- University Level Table -->
    <div v-if="selectedLevel === 'university'" class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 border">
        <thead class="bg-gray-800 text-white">
          <tr>
            <th class="px-4 py-2 text-left">University</th>
            <th class="px-4 py-2 text-left">Responded/Invited</th>
            <th v-for="(question, idx) in getQuestions()" :key="question" class="px-4 py-2 text-left">Q{{ idx + 1 }}
            </th>
            <th class="px-4 py-2 text-left">Average</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr class="hover:bg-gray-50">
            <td class="px-4 py-2">University overall</td>
            <td class="px-4 py-2 text-left">{{ data.responseStats.responded }}/{{ data.responseStats.invited }}</td>
            <td v-for="(question, index) in getQuestions()" :key="question" class="px-4 py-2">
              {{ formatRating(getRating(data.universityResults?.ratings, index)) }}
            </td>
            <td class="px-4 py-2">{{ formatRating(averageRating(data.universityResults.ratings || {})) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination controls -->
    <div v-if="selectedLevel !== 'university' && !fullPage" class="flex justify-between items-center mt-4">
      <button @click="currentPage--" :disabled="currentPage === 1"
        class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md disabled:opacity-50">
        Previous
      </button>
      <span>Page {{ currentPage }} of {{ totalPages }}</span>
      <button @click="currentPage++" :disabled="currentPage === totalPages"
        class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md disabled:opacity-50">
        Next
      </button>
    </div>

    <!-- Comments Modal -->
    <transition name="modal">
      <div v-if="isCommentShow" class="fixed inset-0 flex items-center justify-center z-50">
        <div class="absolute inset-0 bg-black opacity-50"></div>
        <div class="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-2xl sm:w-full">
          <div class="bg-primary text-white px-4 py-2 flex justify-between items-center">
            <h3 class="text-lg leading-6 font-large">Comments of {{ moduleCode }}</h3>
            <button @click="isCommentShow = false" class="text-white font-large">&times;</button>
          </div>
          <div class="px-4 py-5 sm:p-6">
            <table class="min-w-full divide-y divide-gray-200 border mb-4">
              <thead class="bg-black text-white divide-y divide-gray-200">
                <th class="px-4 py-2 text-left">Staff</th>
                <th class="px-4 py-2 text-left">Comments</th>

              </thead>
              <tbody class="bg-white divide-y divide-gray-200" v-if="paginatedComments.length > 0">
                <tr v-for="(comment, index) in paginatedComments" :key="index" class="hover:bg-gray-50">
                  <td class="px-4 py-2 text-left">{{ getStaffName(comment.identity) }}</td>
                  <td class="px-4 py-2 text-left">{{ comment.name }}</td>
                </tr>
              </tbody>
              <tbody v-else>
                No Comments
              </tbody>
            </table>
            <div class="flex justify-between items-center mt-4">
              <button @click="commentsPage--" :disabled="commentsPage === 1"
                class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md disabled:opacity-50">
                Previous
              </button>
              <span>Page {{ commentsPage }} of {{ commentsTotalPages }}</span>
              <button @click="commentsPage++" :disabled="commentsPage === commentsTotalPages"
                class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md disabled:opacity-50">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { surveyApi } from '@/api/surveyManagement';
import { mapState } from 'vuex';

export default {
  name: 'HierarchicalRatingsTable',
  props: {
    surveyId: {
      type: String,
      required: true
    },
    data: {
      type: Object,
      required: true
    },
    selectedLevel: {
      type: String,
      required: true
    },
    fullPage: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 10,
      comments: [],
      commentsPage: 1,
      isCommentShow: false,
      moduleCode: ''
    };
  },
  computed: {
    ...mapState({
      schools: (state) => state.schools,
      departments: (state) => state.departments,
      modules: (state) => state.modules,
      staff: (state) => state.staff,
    }),
    paginatedComments() {
      const start = (this.commentsPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.comments.slice(start, end);
    },
    commentsTotalPages() {
      return Math.ceil(this.comments.length / this.itemsPerPage)
    },
    questions() {
      if (this.selectedLevel == 'module')
        if (this.data.moduleResults && this.data.moduleResults.length > 0) {
          return Object.keys(this.data.moduleResults[0].ratings);
        }
        else {

        }
      return [];
    },
    filteredModuleResults() {
      if (!this.searchQuery || this.searchQuery.length < 3) return this.data.moduleResults || [];
     
      let result = [];
      this.data.moduleResults.forEach(module => {
        if (module.moduleCode.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          module.moduleName.toLowerCase().includes(this.searchQuery.toLowerCase())) {
          result.push(module)
        }
        else {
          let teachers = module.teachers.filter(teacher => teacher.name.toLowerCase().includes(this.searchQuery.toLowerCase()))
          if (teachers.length > 0) {
            result.push({ ...module, teachers: teachers })
          }
        }
      })
      //console.log(result)
      return result
    },
    paginatedModuleResults() {
      if (this.fullPage) return this.filteredModuleResults
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredModuleResults.slice(start, end);
    },
    filteredDepartmentResults() {
      if (!this.searchQuery) return this.data.departmentResults || [];
      return (this.data.departmentResults || []).filter(dept =>
        dept.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    paginatedDepartmentResults() {
      if (this.fullPage) return this.filteredDepartmentResults
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredDepartmentResults.slice(start, end);
    },
    totalPages() {
      let totalItems;
      switch (this.selectedLevel) {
        case 'module':
          totalItems = this.filteredModuleResults.length;
          break;
        case 'department':
          totalItems = this.filteredDepartmentResults.length;
          break;
        case 'school':
          totalItems = Object.keys(this.data.schoolResults?.schoolRatings || {}).length;
          break;
        case 'university':
          totalItems = 1;
          break;
        default:
          totalItems = 1;
      }
      return Math.ceil(totalItems / this.itemsPerPage);
    }
  },
  methods: {
    getSchoolName(shortName){
      return this.schools.find(x=>x.shortName == shortName).fullName
    },
    getStaffName(id){
      return this.staff.find(x=>x.staffId == id) ? this.staff.find(x=>x.staffId == id).name : id
    },
    async showComments(moduleCode) {
      this.moduleCode = moduleCode
      let result = await surveyApi.getSurveyResponseComments(this.surveyId, moduleCode)
      this.isCommentShow = true
      this.comments = result
    },
    getRating(result, index) {
      const keys = Object.keys(result);
      return result[keys[index]];
    },
    getQuestions() {
      console.log(this.selectedLevel)

      if (this.selectedLevel == 'module') {
        if (this.data.moduleResults && this.data.moduleResults.length > 0) {
          return Object.keys(this.data.moduleResults[0].ratings);
        }
      }
      else if (this.selectedLevel == 'department') {
        return Object.keys(this.data.departmentResults[0].ratings);
      }
      else if (this.selectedLevel == 'school') {
        if (this.data.schoolResults && this.data.schoolResults.schoolRatings) {
          const firstSchool = Object.keys(this.data.schoolResults.schoolRatings)[0]
          return Object.keys(this.data.schoolResults.schoolRatings[firstSchool]);
        }
      }
      else
        return Object.keys(this.data.universityResults.ratings);
    },
    formatRating(rating) {
      return rating ? rating == "-" ? "-" : rating.toFixed(2) : '-';
    },
    averageRating(ratings) {
      const validRatings = Object.values(ratings).filter(r => r !== null && r !== undefined);
      return validRatings.length > 0 ? validRatings.reduce((a, b) => a + b, 0) / validRatings.length : '-';
    }
  },
  watch: {
    selectedLevel() {
      this.currentPage = 1;
      this.searchQuery = '';
    },
    searchQuery() {
      this.currentPage = 1;
    }
  }
}
</script>

<style scoped>
.hierarchical-ratings table {
  @apply w-full border-collapse;
}

.hierarchical-ratings th,
.hierarchical-ratings td {
  @apply border px-4 py-2 text-sm;
}

.hierarchical-ratings th {
  @apply font-semibold;
}

.hierarchical-ratings tr:nth-child(even) {
  @apply bg-gray-50;
}

.hierarchical-ratings tr:hover {
  @apply bg-gray-100;
}
</style>